import type { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { DownloadFormat } from "../../../components/UIToolkit/Clickable/DownloadFormatButton";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useGlobalManualLocationOrder } from "../../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useUserSettingsContext } from "../../../contexts/Staffing/UserSettingsContext";
import {
	DEFAULT_HPPD_INCLUDE_TRAINEES,
	DEFAULT_PRINTOUT_FIT_TO_HEIGHT,
	DEFAULT_PRINTOUT_FIT_TO_PAGE,
	DEFAULT_PRINTOUT_FIT_TO_WIDTH,
	DEFAULT_SHOW_ACTUAL_HOURS_COLUMN,
	DEFAULT_SHOW_ACTUAL_STAFF_COLUMN,
	DEFAULT_SHOW_ADMINS,
	DEFAULT_SHOW_DHPPD,
	DEFAULT_SHOW_NHPPD,
} from "../../../contexts/Staffing/UserSettingsProvider";
import { useWeekViewContext } from "../../../contexts/WeekView";
import type { ShiftTimeAnchor } from "../../../downloads/dailyPrintout/types";
import { createDailySchedule } from "../../../downloads/downloadDayView";
import { create530Excel, form530PageSetup } from "../../../downloads/form530/create530Excel";
import type { Form530Settings } from "../../../downloads/form530/pages";
import { handleDownload } from "../../../downloads/utils";
import { useCensusPointInTimesLoader } from "../../../hooks/useCensusPointInTimes";
import { useLastNameFirst } from "../../../hooks/useNameFormat";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { isCaliforniaDepartment } from "../../../utils/californiaDepartment";
import type { SheetStyleOverlay } from "../../../utils/fortuneSheetStyleOverlay";
import {
	type BreakRulesConfig,
	type CalledOffShift,
	type CustomColumnNamesConfig,
	type CustomSheetTabsConfig,
	type CustomTimeGroupsConfig,
	type DailyPrintoutGrouping,
	DailyPrintoutGroupingOption,
	type Nurse,
	type NurseFullRole,
	type NurseInShift,
	type PrintoutCustomizableColumnType,
	PrintoutOrientation,
	type PrintoutShowAdminsConfig,
	type Shift,
} from "../../../utils/models";
import { computeDirectAndNursingHppd } from "../../../utils/statsCalculations";

type UseDayScheduleDownloadParams = { date: DateTime; selectedLocations: string[]; availableLocations: string[]; showActual?: boolean };

export type DownloadType = "detailed" | "summary" | "530" | "spreadsheet" | "custom";

// Column types available for printout
export type PrintoutColumnType = PrintoutCustomizableColumnType;

// For backward compatibility
export type PrintoutColumnId = PrintoutColumnType;

export type SortDirection = "asc" | "desc";

export type PrintoutColumn = {
	type: PrintoutColumnType;
	enabled: boolean;
	sortDirection?: SortDirection;
};

// When JobManagementEnabled is on, the "role" column / grouping option is presented as "Job"
// in the Printout Settings panel. The persisted setting still uses PrintoutColumnType "role".
export const usePrintoutColumnLabel = () => {
	const { t } = useTranslation();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	return useCallback(
		(type: PrintoutColumnType): string => {
			if (type === "role" && jobManagementEnabled) return t("printout.column.job");
			return t(`printout.column.${type}`);
		},
		[jobManagementEnabled, t],
	);
};

export const DEFAULT_PRINTOUT_COLUMNS: PrintoutColumn[] = [
	{ type: "location", enabled: true },
	{ type: "name", enabled: true },
	{ type: "certification", enabled: true },
	{ type: "shiftTime", enabled: true },
];

export const AVAILABLE_COLUMN_TYPES: PrintoutColumnType[] = [
	"location",
	"name",
	"certification",
	"shiftTime",
	"role",
	"notes",
	"initials",
	"floor",
	"scheduledHours",
];

export const DEFAULT_SORT_BY: PrintoutColumn[] = [{ type: "name", enabled: true, sortDirection: "asc" }];

export type PrintoutSettings = {
	printGaps: boolean;
	showRoleGroupStats: boolean;
	dailyPrintoutGrouping: DailyPrintoutGrouping;
	divideGroupsByPage: boolean;
	separateSheetsBy: DailyPrintoutGroupingOption;
	showPtoSheet: boolean;
	showCallOutsSheet: boolean;
	includeTimeOffReason: boolean;
	columns: PrintoutColumn[];
	sortBy: PrintoutColumn[];
	staffPerRow: number;
	rowGroupingBy: PrintoutColumnType[];
	columnGroupingBy: PrintoutColumnType[] | null;
	spaceBetweenGroups: boolean;
	orientation: PrintoutOrientation;
	fitToPage: boolean;
	fitToWidth: number;
	fitToHeight: number;
	hideHppdNursingHours: boolean;
	printoutColumnWidths?: Record<string, number>;
	flagNotOnboarded: boolean;
	flagAgency: boolean;
	mergeSimilarShiftTimes: boolean;
	alignGroupTimesToPopular: boolean;
	customSheetTabs: CustomSheetTabsConfig;
	showAdmins: PrintoutShowAdminsConfig;
	showLocationCensus: boolean;
	customTimeGroups: CustomTimeGroupsConfig;
	appendRoleToName: boolean;
	hideColumnNames: boolean;
	appendEmptyColumns: number;
	customColumnNames: CustomColumnNamesConfig;
	// Summary-mode-only settings
	showDhppd: boolean;
	showNhppd: boolean;
	showActualStaffColumn: boolean;
	showActualHoursColumn: boolean;
	hppdIncludeTrainees: boolean;
};

/**
 * The summary-mode DHPPD/NHPPD values (`computeDirectAndNursingHppd`). Zeros for non-summary downloads, which
 * ignore the values.
 */
const computeSummaryHppd = (
	summary: boolean,
	weeklyShifts: Shift[],
	dayIndex: number,
	showActual: boolean | undefined,
	isSnf: boolean,
	rolesToDisplay: NurseFullRole[],
	departmentId: string,
	breakRulesConfig: BreakRulesConfig[],
	jobManagementEnabled: boolean,
	allNurses: Nurse[],
	includeTrainees: boolean,
	census: number,
): { dhppd: number; nhppd: number } => {
	if (!summary) return { dhppd: 0, nhppd: 0 };
	return computeDirectAndNursingHppd({
		weeklyShifts,
		dayIndex,
		showActual,
		isSnf,
		rolesToDisplay,
		departmentId,
		breakRulesConfig,
		jobManagementEnabled,
		allNurses,
		includeTrainees,
		census,
	});
};

export const usePrintout = ({ date, selectedLocations, availableLocations, showActual }: UseDayScheduleDownloadParams) => {
	const { t } = useTranslation();
	const { isLowry, hospitalData, chosenDepartment, isAnyDemo, showErrorSnackbar, getWeekIndexByDate, isHospital, departmentNurseTypes, getNurseById } =
		useHospitalData();
	const { weekViewData } = useWeekViewContext();
	const { showSnackbar } = useSnackbar();
	const { settings } = useUserSettingsContext();
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const { userInfo } = useAuth();
	const { track, trackError } = useAnalytics();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const aiDesignedPrintoutEnabled = useFeatureFlag(FEATURE_FLAGS.AiDesignedPrintout);
	const lastNameFirst = useLastNameFirst();

	// Both CDPH surfaces — the 530 form and the §72329.2 census readings — are California rules, so they
	// share one predicate and one gate. No feature flag: the state is the whole condition, as it is for
	// the 530 download below.
	const isCalifornia = isCaliforniaDepartment(chosenDepartment.timezone, isAnyDemo);

	const downloadOptions = useMemo(() => {
		const options: { label: string; value: DownloadType }[] = [
			{ label: t("download.detailed"), value: "detailed" },
			{ label: t("download.summary"), value: "summary" },
		];

		if (isCalifornia) options.push({ label: t("download.530"), value: "530" });

		if (isLowry) options.push({ label: t("download.spreadsheet"), value: "spreadsheet" });

		if (aiDesignedPrintoutEnabled) options.push({ label: t("download.custom"), value: "custom" });

		return options;
	}, [t, isCalifornia, isLowry, aiDesignedPrintoutEnabled]);

	/**
	 * A missing set of readings does not fail the printout — the schedule is still worth printing — but
	 * the user is told this copy lacks the compliance numbers, and the error is tracked.
	 */
	const reportCensusPointInTimesFailure = useCallback(
		(error: unknown) => {
			trackError("census_point_in_times_failed", {
				department_id: chosenDepartment.department_id,
				date: date.toISODate(),
				error_message: error instanceof Error ? error.message : String(error),
			});
			showSnackbar(t("error.census.point.in.times.unavailable"), "warning");
		},
		[chosenDepartment.department_id, date, showSnackbar, t, trackError],
	);

	const loadCensusPointInTimes = useCensusPointInTimesLoader(chosenDepartment.department_id, isCalifornia, reportCensusPointInTimesFailure);

	const handleDayViewDownload = useCallback(
		async (
			summary: boolean,
			callOutShifts: CalledOffShift[],
			isLowrySpreadsheet?: boolean,
			printoutSettings?: PrintoutSettings,
			shiftTimeAnchors?: ShiftTimeAnchor[],
			/** Session-only cell formatting from the printout preview. Trailing and optional, so the
			 *  callers that have no preview are unchanged. */
			styleOverlay?: SheetStyleOverlay,
		): Promise<void> => {
			try {
				const dayItem = weekViewData.at(date.weekdayISO);
				if (!dayItem) return;

				const weekIndex = getWeekIndexByDate(date);
				if (isLowrySpreadsheet) {
					for (let shift = 0; shift < hospitalData.weeks[weekIndex].weeklyShifts.length; shift++) {
						const nurse = hospitalData.weeks[weekIndex].weeklyShifts[shift].days[date.weekdayISO].nurses.find((n) => !n.location);
						if (nurse) {
							showSnackbar(t("error.download.nurse.no.location", { nurseName: nurse.name }), "warning");
							return;
						}
					}
				}
				const dayOfWeek = date.toFormat("ccc");
				const effectiveSettings = printoutSettings ?? {
					printGaps: settings.printGaps,
					showRoleGroupStats: settings.showRoleGroupStats ?? false,
					dailyPrintoutGrouping: settings.dailyPrintoutGrouping,
					divideGroupsByPage: settings.divideGroupsByPage ?? false,
					separateSheetsBy: settings.separateSheetsBy ?? DailyPrintoutGroupingOption.NONE,
					showPtoSheet: settings.showPtoSheet ?? false,
					showCallOutsSheet: settings.showCallOutsSheet ?? false,
					includeTimeOffReason: settings.includeTimeOffReason ?? true,
					columns: (settings.printoutColumns as PrintoutColumn[]) ?? DEFAULT_PRINTOUT_COLUMNS,
					sortBy: (settings.printoutSortBy as PrintoutColumn[]) ?? DEFAULT_SORT_BY,
					staffPerRow: settings.staffPerRow ?? 1,
					rowGroupingBy: (settings.rowGroupingBy as PrintoutColumnType[]) ?? [],
					columnGroupingBy: (settings.columnGroupingBy as PrintoutColumnType[] | null) ?? null,
					spaceBetweenGroups: settings.spaceBetweenGroups ?? true,
					orientation: settings.printoutOrientation ?? PrintoutOrientation.Landscape,
					fitToPage: settings.printoutFitToPage ?? DEFAULT_PRINTOUT_FIT_TO_PAGE,
					fitToWidth: settings.printoutFitToWidth ?? DEFAULT_PRINTOUT_FIT_TO_WIDTH,
					fitToHeight: settings.printoutFitToHeight ?? DEFAULT_PRINTOUT_FIT_TO_HEIGHT,
					hideHppdNursingHours: settings.hideHppdNursingHours ?? false,
					printoutColumnWidths: settings.printoutColumnWidths,
					flagNotOnboarded: settings.flagNotOnboarded ?? true,
					flagAgency: settings.flagAgency ?? true,
					mergeSimilarShiftTimes: settings.mergeSimilarShiftTimes ?? false,
					alignGroupTimesToPopular: settings.alignGroupTimesToPopular ?? false,
					customTimeGroups: settings.customTimeGroups,
					appendRoleToName: settings.appendRoleToName,
					hideColumnNames: settings.hideColumnNames,
					appendEmptyColumns: settings.appendEmptyColumns,
					customColumnNames: settings.customColumnNames,
					customSheetTabs: settings.customSheetTabs ?? { tabs: [] },
					showAdmins: settings.showAdmins ?? DEFAULT_SHOW_ADMINS,
					showLocationCensus: settings.showLocationCensus ?? false,
					showDhppd: settings.showDhppd ?? DEFAULT_SHOW_DHPPD,
					showNhppd: settings.showNhppd ?? DEFAULT_SHOW_NHPPD,
					showActualStaffColumn: settings.showActualStaffColumn ?? DEFAULT_SHOW_ACTUAL_STAFF_COLUMN,
					showActualHoursColumn: settings.showActualHoursColumn ?? DEFAULT_SHOW_ACTUAL_HOURS_COLUMN,
					hppdIncludeTrainees: settings.hppdIncludeTrainees ?? DEFAULT_HPPD_INCLUDE_TRAINEES,
				};
				const printNotes = effectiveSettings.columns.some((c) => c.type === "notes" && c.enabled);
				// Summary-mode DHPPD/NHPPD numbers, computed via the shared getTotalHours (reuses role/break logic).
				// DHPPD includes ppd-excluded staff; NHPPD excludes them; both honor the include-trainees toggle.
				const summaryDayStats = showActual ? dayItem.dayStatsActual : dayItem.dayStats;
				const { dhppd, nhppd } = computeSummaryHppd(
					summary,
					hospitalData.weeks[weekIndex].weeklyShifts,
					date.weekdayISO,
					showActual,
					!isHospital,
					departmentNurseTypes,
					chosenDepartment.department_id,
					chosenDepartment.breakRulesConfig ?? [],
					jobManagementEnabled,
					hospitalData.nurses,
					effectiveSettings.hppdIncludeTrainees,
					summaryDayStats.census,
				);
				const censusPointInTimes = isLowrySpreadsheet ? undefined : await loadCensusPointInTimes(date);
				const xlsxBuffer = await createDailySchedule({
					hospitalData,
					weekIndex,
					dayIndex: date.weekdayISO,
					department: chosenDepartment,
					day: dayOfWeek,
					date: date,
					isLowrySpreadsheet,
					summary,
					printNotes,
					printGaps: effectiveSettings.printGaps,
					showRoleGroupStats: effectiveSettings.showRoleGroupStats,
					printoutGroupBy: effectiveSettings.dailyPrintoutGrouping,
					dayStats: showActual ? dayItem.dayStatsActual : dayItem.dayStats,
					nurses: showActual ? dayItem.shiftsActual : dayItem.shifts,
					selectedLocations,
					availableLocations,
					divideGroupsByPage: effectiveSettings.divideGroupsByPage,
					userId: userInfo.id,
					separateSheetsBy: effectiveSettings.separateSheetsBy,
					customSheetTabs: effectiveSettings.customSheetTabs,
					showAdmins: effectiveSettings.showAdmins,
					showLocationCensus: effectiveSettings.showLocationCensus,
					showPtoSheet: effectiveSettings.showPtoSheet,
					callOutShifts,
					includeTimeOffReason: effectiveSettings.includeTimeOffReason,
					columns: effectiveSettings.columns,
					sortBy: effectiveSettings.sortBy,
					staffPerRow: effectiveSettings.staffPerRow,
					rowGroupingBy: effectiveSettings.rowGroupingBy,
					columnGroupingBy: effectiveSettings.columnGroupingBy,
					spaceBetweenGroups: effectiveSettings.spaceBetweenGroups,
					orientation: effectiveSettings.orientation,
					fitToPage: effectiveSettings.fitToPage,
					fitToWidth: effectiveSettings.fitToWidth,
					fitToHeight: effectiveSettings.fitToHeight,
					hideHppdNursingHours: effectiveSettings.hideHppdNursingHours,
					printoutColumnWidths: effectiveSettings.printoutColumnWidths,
					flagNotOnboarded: effectiveSettings.flagNotOnboarded,
					flagAgency: effectiveSettings.flagAgency,
					mergeSimilarShiftTimes: effectiveSettings.mergeSimilarShiftTimes,
					alignGroupTimesToPopular: effectiveSettings.alignGroupTimesToPopular,
					customTimeGroups: effectiveSettings.customTimeGroups,
					customTimeGroupsOtherLabel: t("printout.custom.unmatched.tab"),
					appendRoleToName: effectiveSettings.appendRoleToName,
					hideColumnNames: effectiveSettings.hideColumnNames,
					appendEmptyColumns: effectiveSettings.appendEmptyColumns,
					customColumnNames: effectiveSettings.customColumnNames,
					showDhppd: effectiveSettings.showDhppd,
					showNhppd: effectiveSettings.showNhppd,
					showActualStaffColumn: effectiveSettings.showActualStaffColumn,
					showActualHoursColumn: effectiveSettings.showActualHoursColumn,
					dhppd,
					nhppd,
					shiftTimeAnchors,
					customStringOrderMap,
					jobManagementEnabled,
					lastNameFirst,
					censusPointInTimes,
					styleOverlay,
				});
				handleDownload(xlsxBuffer, `daily_schedule_${date.toFormat("yyyy-MM-dd")}.xlsx`);
				showSnackbar(t("printout.download.success"), "success");
			} catch (error) {
				showErrorSnackbar(t("failed.to.download.day.view"), {}, error);
			}
		},
		[
			chosenDepartment,
			hospitalData,
			showErrorSnackbar,
			date,
			settings,
			t,
			availableLocations,
			selectedLocations,
			userInfo.id,
			showSnackbar,
			getWeekIndexByDate,
			weekViewData,
			showActual,
			customStringOrderMap,
			jobManagementEnabled,
			lastNameFirst,
			isHospital,
			departmentNurseTypes,
			loadCensusPointInTimes,
		],
	);

	// The 530's "EMPLOYEE NAME" column sorts by surname, so names are rendered "Last, First" from the
	// nurse record rather than taken from the shift's own display name.
	const format530Name = useCallback(
		(nurse: NurseInShift): string => {
			const fullNurse = getNurseById(nurse.nurseId);
			// DEFAULT: fall back to the existing display name if the nurse isn't in the map (shouldn't happen).
			return fullNurse ? `${fullNurse.lastName}, ${fullNurse.firstName}` : nurse.name;
		},
		[getNurseById],
	);

	/** The roster inputs every 530 renderer needs, or null when the day isn't loaded. */
	const form530Source = useCallback(() => {
		const dayItem = weekViewData.at(date.weekdayISO);
		if (!dayItem) return null;
		return {
			scheduledNurses: dayItem.shifts,
			actualNurses: dayItem.shiftsActual,
			department: chosenDepartment,
			date,
			formatName: format530Name,
		};
	}, [weekViewData, date, chosenDepartment, format530Name]);

	/**
	 * The 530 as an Excel workbook — used for both the preview and the xlsx download, so what the user
	 * approves on screen is the file they get. Page setup is the FORM's own (`form530PageSetup`), driven
	 * by the one page-setup choice the 530 tab offers: the scale-to-fit switch.
	 */
	const generate530Buffer = useCallback(
		async (form530: Form530Settings, printoutSettings: PrintoutSettings): Promise<ArrayBuffer | null> => {
			const source = form530Source();
			if (!source) return null;
			return await create530Excel({ ...source, settings: form530, pageSetup: form530PageSetup(printoutSettings.fitToPage) });
		},
		[form530Source],
	);

	/**
	 * Downloads the 530 in either format under one filename and one tracking event.
	 *
	 * Only the workbook is built here. The PDF is that same workbook rendered by the shared printout
	 * renderer, which needs the converted preview sheets — those live in the download dialog, so it
	 * hands in `buildPdf` rather than the hook reaching for them. Lazy so the xlsx path never renders one.
	 *
	 * Returns whether a file actually reached the browser. The error is reported here (this owns the
	 * translated message), but the caller still has to know it failed: closing the dialog on a failed
	 * export throws away the preview and settings the user needs in order to retry.
	 */
	const handle530Download = useCallback(
		async (form530: Form530Settings, format: DownloadFormat, printoutSettings: PrintoutSettings, buildPdf: () => Promise<Blob | null>): Promise<boolean> => {
			try {
				const fileBase = `cdph_530_${date.toFormat("yyyy-MM-dd")}`;
				if (format === "xlsx") {
					const buffer = await generate530Buffer(form530, printoutSettings);
					// Thrown rather than returned: there is no workbook for the day the user is looking at, and
					// a download that quietly does nothing looks identical to a browser that blocked it.
					if (!buffer) throw new Error("530 workbook generation produced no file");
					handleDownload(buffer, `${fileBase}.xlsx`);
				} else {
					const pdfBlob = await buildPdf();
					if (!pdfBlob) throw new Error("530 PDF rendering produced no file");
					handleDownload(pdfBlob, `${fileBase}.pdf`, "application/pdf");
				}
				track("download_day_view_530_success", { format });
				return true;
			} catch (error) {
				showErrorSnackbar(t("failed.to.download.day.view"), {}, error);
				return false;
			}
		},
		[generate530Buffer, date, showErrorSnackbar, track, t],
	);

	const generatePreviewBuffer = useCallback(
		async (
			downloadType: DownloadType,
			printoutSettings: PrintoutSettings,
			callOutShifts: CalledOffShift[],
			shiftTimeAnchors: ShiftTimeAnchor[] | undefined,
			form530: Form530Settings,
			/**
			 * Session formatting to bake into the workbook. Omitted for the preview itself (there is no
			 * formatting until the user applies some to it), and passed when the PDF path rebuilds the
			 * workbook so the bytes it renders are the bytes the xlsx download would hand over.
			 */
			styleOverlay?: SheetStyleOverlay,
		): Promise<ArrayBuffer | null> => {
			try {
				const weekIndex = getWeekIndexByDate(date);

				// The 530 is its own workbook — a fixed government form, not the configurable day view.
				if (downloadType === "530") return await generate530Buffer(form530, printoutSettings);

				const dayItem = weekViewData.at(date.weekdayISO);
				if (!dayItem) return null;

				const dayOfWeek = date.toFormat("ccc");
				const summary = downloadType === "summary";
				const isLowrySpreadsheet = downloadType === "spreadsheet";
				const printNotes = printoutSettings.columns.some((c) => c.type === "notes" && c.enabled);
				const summaryDayStats = showActual ? dayItem.dayStatsActual : dayItem.dayStats;
				const { dhppd, nhppd } = computeSummaryHppd(
					summary,
					hospitalData.weeks[weekIndex].weeklyShifts,
					date.weekdayISO,
					showActual,
					!isHospital,
					departmentNurseTypes,
					chosenDepartment.department_id,
					chosenDepartment.breakRulesConfig ?? [],
					jobManagementEnabled,
					hospitalData.nurses,
					printoutSettings.hppdIncludeTrainees,
					summaryDayStats.census,
				);
				const censusPointInTimes = isLowrySpreadsheet ? undefined : await loadCensusPointInTimes(date);

				return await createDailySchedule({
					hospitalData,
					weekIndex,
					dayIndex: date.weekdayISO,
					department: chosenDepartment,
					day: dayOfWeek,
					date: date,
					isLowrySpreadsheet,
					summary,
					printNotes,
					printGaps: printoutSettings.printGaps,
					showRoleGroupStats: printoutSettings.showRoleGroupStats,
					printoutGroupBy: printoutSettings.dailyPrintoutGrouping,
					dayStats: showActual ? dayItem.dayStatsActual : dayItem.dayStats,
					nurses: showActual ? dayItem.shiftsActual : dayItem.shifts,
					selectedLocations,
					availableLocations,
					divideGroupsByPage: printoutSettings.divideGroupsByPage,
					userId: userInfo.id,
					separateSheetsBy: printoutSettings.separateSheetsBy,
					customSheetTabs: printoutSettings.customSheetTabs,
					showAdmins: printoutSettings.showAdmins,
					showLocationCensus: printoutSettings.showLocationCensus,
					showPtoSheet: printoutSettings.showPtoSheet,
					callOutShifts,
					includeTimeOffReason: printoutSettings.includeTimeOffReason,
					columns: printoutSettings.columns,
					sortBy: printoutSettings.sortBy,
					staffPerRow: printoutSettings.staffPerRow,
					rowGroupingBy: printoutSettings.rowGroupingBy,
					spaceBetweenGroups: printoutSettings.spaceBetweenGroups,
					orientation: printoutSettings.orientation,
					fitToPage: printoutSettings.fitToPage,
					fitToWidth: printoutSettings.fitToWidth,
					fitToHeight: printoutSettings.fitToHeight,
					hideHppdNursingHours: printoutSettings.hideHppdNursingHours,
					printoutColumnWidths: printoutSettings.printoutColumnWidths,
					columnGroupingBy: printoutSettings.columnGroupingBy,
					flagNotOnboarded: printoutSettings.flagNotOnboarded,
					flagAgency: printoutSettings.flagAgency,
					mergeSimilarShiftTimes: printoutSettings.mergeSimilarShiftTimes,
					alignGroupTimesToPopular: printoutSettings.alignGroupTimesToPopular,
					customTimeGroups: printoutSettings.customTimeGroups,
					customTimeGroupsOtherLabel: t("printout.custom.unmatched.tab"),
					appendRoleToName: printoutSettings.appendRoleToName,
					hideColumnNames: printoutSettings.hideColumnNames,
					appendEmptyColumns: printoutSettings.appendEmptyColumns,
					customColumnNames: printoutSettings.customColumnNames,
					showDhppd: printoutSettings.showDhppd,
					showNhppd: printoutSettings.showNhppd,
					showActualStaffColumn: printoutSettings.showActualStaffColumn,
					showActualHoursColumn: printoutSettings.showActualHoursColumn,
					dhppd,
					nhppd,
					shiftTimeAnchors,
					customStringOrderMap,
					jobManagementEnabled,
					lastNameFirst,
					censusPointInTimes,
					styleOverlay,
				});
			} catch (error) {
				showErrorSnackbar("Failed to generate preview", {}, error);
				return null;
			}
		},
		// `settings` is intentionally excluded: it was only used as a fallback when printoutSettings
		// was not passed, which never happens. Including it caused generatePreviewBuffer to be
		// recreated on every settings load, triggering loadPreview before printoutSettings state
		// had been updated, showing a preview with stale/default settings.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			chosenDepartment,
			hospitalData,
			date,
			selectedLocations,
			availableLocations,
			userInfo.id,
			getWeekIndexByDate,
			weekViewData,
			showErrorSnackbar,
			showActual,
			customStringOrderMap,
			generate530Buffer,
			jobManagementEnabled,
			lastNameFirst,
			isHospital,
			departmentNurseTypes,
			loadCensusPointInTimes,
			t,
		],
	);

	const defaultPrintoutSettings: PrintoutSettings = useMemo(
		() => ({
			printGaps: settings.printGaps,
			showRoleGroupStats: settings.showRoleGroupStats ?? false,
			dailyPrintoutGrouping: settings.dailyPrintoutGrouping,
			divideGroupsByPage: settings.divideGroupsByPage ?? false,
			separateSheetsBy: settings.separateSheetsBy ?? DailyPrintoutGroupingOption.NONE,
			showPtoSheet: settings.showPtoSheet ?? false,
			showCallOutsSheet: settings.showCallOutsSheet ?? false,
			includeTimeOffReason: settings.includeTimeOffReason ?? true,
			columns: (settings.printoutColumns as PrintoutColumn[]) ?? DEFAULT_PRINTOUT_COLUMNS,
			sortBy: (settings.printoutSortBy as PrintoutColumn[]) ?? DEFAULT_SORT_BY,
			staffPerRow: settings.staffPerRow ?? 1,
			rowGroupingBy: (settings.rowGroupingBy as PrintoutColumnType[]) ?? [],
			columnGroupingBy: (settings.columnGroupingBy as PrintoutColumnType[]) ?? null,
			spaceBetweenGroups: settings.spaceBetweenGroups ?? true,
			orientation: settings.printoutOrientation ?? PrintoutOrientation.Landscape,
			fitToPage: settings.printoutFitToPage ?? DEFAULT_PRINTOUT_FIT_TO_PAGE,
			fitToWidth: settings.printoutFitToWidth ?? DEFAULT_PRINTOUT_FIT_TO_WIDTH,
			fitToHeight: settings.printoutFitToHeight ?? DEFAULT_PRINTOUT_FIT_TO_HEIGHT,
			hideHppdNursingHours: settings.hideHppdNursingHours ?? false,
			printoutColumnWidths: settings.printoutColumnWidths,
			flagNotOnboarded: settings.flagNotOnboarded ?? true,
			flagAgency: settings.flagAgency ?? true,
			mergeSimilarShiftTimes: settings.mergeSimilarShiftTimes ?? false,
			alignGroupTimesToPopular: settings.alignGroupTimesToPopular ?? false,
			customTimeGroups: settings.customTimeGroups,
			appendRoleToName: settings.appendRoleToName,
			hideColumnNames: settings.hideColumnNames,
			appendEmptyColumns: settings.appendEmptyColumns,
			customColumnNames: settings.customColumnNames,
			customSheetTabs: settings.customSheetTabs ?? { tabs: [] },
			showAdmins: settings.showAdmins ?? DEFAULT_SHOW_ADMINS,
			showLocationCensus: settings.showLocationCensus ?? false,
			showDhppd: settings.showDhppd ?? DEFAULT_SHOW_DHPPD,
			showNhppd: settings.showNhppd ?? DEFAULT_SHOW_NHPPD,
			showActualStaffColumn: settings.showActualStaffColumn ?? DEFAULT_SHOW_ACTUAL_STAFF_COLUMN,
			showActualHoursColumn: settings.showActualHoursColumn ?? DEFAULT_SHOW_ACTUAL_HOURS_COLUMN,
			hppdIncludeTrainees: settings.hppdIncludeTrainees ?? DEFAULT_HPPD_INCLUDE_TRAINEES,
		}),
		[settings],
	);

	return {
		handleDayViewDownload,
		handle530Download,
		generatePreviewBuffer,
		show530: isCalifornia,
		downloadOptions,
		defaultPrintoutSettings,
	};
};
